import { Control, Controller, useController } from "react-hook-form";
import MonacoEditor, { loader } from "@monaco-editor/react";
import { AlertCircle, HelpCircle } from "lucide-react";
import * as yup from "yup";
import { Alert, AlertDescription } from "~/components/ui/alert";
import ChipsMultiSelect, { ChipsMultiSelectOption } from "~/components/ui/chips-multi-select";
import { BaseInput } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useDebounceFalsy } from "~/hooks/useDebounceFalsy";
import { cn } from "~/lib/utils";
import * as monaco from "~/utils/monaco-editor";

loader.config({ monaco });

export type SkillTag = "ORCHESTRATION" | "ANALYSIS";

export interface SkillFormData {
  name: string;
  description: string;
  tags: SkillTag[];
  content: string;
}

export interface SkillFormErrors {
  name?: string;
  description?: string;
  tags?: string;
  content?: string;
  general?: string;
}

export const SkillEditorMode = {
  CREATE: "create",
  EDIT: "edit",
} as const;
export type SkillEditorMode = (typeof SkillEditorMode)[keyof typeof SkillEditorMode];

const AVAILABLE_TAGS: ChipsMultiSelectOption<SkillTag>[] = [
  {
    value: "ORCHESTRATION",
    label: "Orchestration",
    description: "Skills that help coordinate and manage workflows",
  },
  {
    value: "ANALYSIS",
    label: "Analysis",
    description: "Skills that help analyze and interpret data",
  },
];

export const DEFAULT_CONTENT = `When using this skill, always:

1. **First step**: Describe what to do first
2. **Second step**: Describe what to do next
3. **Third step**: Additional instructions

Keep responses clear and actionable.
`;

export const skillValidationSchema = yup.object({
  name: yup
    .string()
    .required("Skill name is required")
    .min(3, "Skill name must be at least 3 characters")
    .max(64, "Skill name must be 64 characters or less")
    .matches(/^[a-z0-9-]+$/, "Use only lowercase letters, numbers, and hyphens")
    .test("starts-with-letter", "Skill name must start with a letter", (value) =>
      value ? /^[a-z]/.test(value) : true
    )
    .test("no-trailing-hyphen", "Skill name cannot end with a hyphen", (value) =>
      value ? !value.endsWith("-") : true
    ),
  description: yup.string().required("Description is required"),
  content: yup.string().required("Instructions are required"),
  tags: yup
    .array()
    .of(yup.mixed<SkillTag>().oneOf(["ORCHESTRATION", "ANALYSIS"]).required())
    .required()
    .default([]),
});

interface SkillNameFieldProps {
  control: Control<SkillFormData>;
  loading: boolean;
  nameDisabled: boolean;
}

const SkillNameField = ({ control, loading, nameDisabled }: SkillNameFieldProps) => {
  const { field, fieldState } = useController({ name: "name", control });
  const debouncedError = useDebounceFalsy(fieldState.error?.message, 0, 300);

  return (
    <BaseInput
      {...field}
      placeholder="my-skill-name"
      disabled={loading || nameDisabled}
      errorMessage={debouncedError}
      aria-invalid={!!debouncedError}
      data-1p-ignore
      topElement={
        <div className="flex items-center gap-1">
          <Label className="gap-0">
            Name <span className="text-destructive">*</span>
          </Label>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-[220px] z-[1300]">
              Use lowercase letters, numbers, and hyphens. This is how you&apos;ll invoke the skill
              (e.g., /my-skill-name).
            </TooltipContent>
          </Tooltip>
        </div>
      }
    />
  );
};

interface SkillEditorFormProps {
  control: Control<SkillFormData>;
  errors?: SkillFormErrors;
  loading?: boolean;
  mode?: SkillEditorMode;
}

const SkillEditorForm = ({
  control,
  errors = {},
  loading = false,
  mode = SkillEditorMode.CREATE,
}: SkillEditorFormProps) => {
  return (
    <div className="px-6 pt-4 pb-4 overflow-y-auto flex-1">
      {errors.general && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle />
          <AlertDescription className="whitespace-pre-line">{errors.general}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <SkillNameField
          control={control}
          loading={loading}
          nameDisabled={mode === SkillEditorMode.EDIT}
        />

        <Controller
          name="description"
          control={control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <Label htmlFor="skill-description" className="gap-0">
                Description <span className="text-destructive">*</span>
              </Label>
              <BaseInput
                {...field}
                id="skill-description"
                placeholder="What this skill does and when to use it..."
                disabled={loading}
                errorMessage={fieldState.error?.message}
                aria-invalid={!!fieldState.error}
              />
            </div>
          )}
        />

        <Controller
          name="tags"
          control={control}
          render={({ field }) => (
            <ChipsMultiSelect
              label="Tags"
              selectedValues={field.value}
              options={AVAILABLE_TAGS}
              onChange={field.onChange}
              disabled={loading}
            />
          )}
        />

        <Controller
          name="content"
          control={control}
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-2">
              <Label htmlFor="skill-content" className="gap-0">
                Instructions <span className="text-destructive">*</span>
              </Label>
              <div
                className={cn(
                  "border rounded-md overflow-hidden",
                  fieldState.error && "border-destructive"
                )}
              >
                <MonacoEditor
                  height="300px"
                  language="markdown"
                  value={field.value}
                  onChange={(v) => field.onChange(v ?? "")}
                  options={{
                    automaticLayout: true,
                    fixedOverflowWidgets: true,
                    formatOnPaste: true,
                    formatOnType: true,
                    minimap: { enabled: false },
                    readOnly: loading,
                    scrollBeyondLastLine: false,
                    wordBasedSuggestions: "off",
                    wordWrap: "on",
                  }}
                />
              </div>
              {fieldState.error && (
                <span className="text-xs text-destructive">{fieldState.error.message}</span>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default SkillEditorForm;
